import { Controller } from '@hotwired/stimulus'

/**
 * Stimulus controller for generating cover images with DALL-E
 *
 * Usage in ERB:
 * <div data-controller="admin--image-generator"
 *      data-admin--image-generator-article-id-value="<%= article.id %>"
 *      data-admin--image-generator-generate-url-value="<%= generate_cover_image_admin_resource_article_path(article) %>"
 *      data-admin--image-generator-suggest-url-value="<%= suggest_prompt_admin_resource_article_path(article) %>">
 *   ...
 * </div>
 */
export default class extends Controller {
  static values = {
    articleId: String,
    generateUrl: String,
    suggestUrl: String
  }

  static targets = [
    'prompt',
    'hiddenPrompt',
    'preview',
    'previewImage',
    'status',
    'cost',
    'generateBtn',
    'removeBtn',
    'spinner'
  ]

  connect() {
    this.isGenerating = false

    // Sync textarea with hidden form field on changes
    if (this.hasPromptTarget) {
      this.promptTarget.addEventListener('input', () => this.syncHiddenPrompt())
    }
  }

  syncHiddenPrompt() {
    if (this.hasHiddenPromptTarget && this.hasPromptTarget) {
      this.hiddenPromptTarget.value = this.promptTarget.value
    }
  }

  async generate(event) {
    event.preventDefault()

    if (this.isGenerating) return

    const prompt = this.hasPromptTarget ? this.promptTarget.value : null
    const force = event.currentTarget.dataset.force === 'true'

    this.setGenerating(true)
    this.setStatus('Generating image with DALL-E...')

    try {
      const response = await fetch(this.generateUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify({ prompt, force })
      })

      const data = await response.json()

      if (data.success) {
        if (data.skipped) {
          this.setStatus('Image already exists. Use force to regenerate.')
        } else {
          this.updatePreview(data.thumb_url || data.url)
          this.updateCost(data.cost_cents)
          this.setStatus('Image generated successfully!')
          setTimeout(() => this.clearStatus(), 3000)
        }
      } else {
        this.setStatus(`Error: ${data.message || data.error}`, true)
      }
    } catch (error) {
      this.setStatus(`Error: ${error.message}`, true)
    } finally {
      this.setGenerating(false)
    }
  }

  async suggestPrompt(event) {
    event.preventDefault()

    if (!this.hasPromptTarget || !this.suggestUrlValue) return

    const button = event.currentTarget
    const originalText = button.textContent
    button.textContent = 'Loading...'
    button.disabled = true

    try {
      const response = await fetch(this.suggestUrlValue, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      })

      const data = await response.json()

      if (data.success) {
        this.promptTarget.value = data.prompt
        this.syncHiddenPrompt()
        this.setStatus('Prompt suggested. You can edit it before generating.')
        setTimeout(() => this.clearStatus(), 3000)
      } else {
        this.setStatus(`Error: ${data.message || data.error}`, true)
      }
    } catch (error) {
      this.setStatus(`Error: ${error.message}`, true)
    } finally {
      button.textContent = originalText
      button.disabled = false
    }
  }

  updatePreview(url) {
    if (this.hasPreviewTarget && this.hasPreviewImageTarget) {
      this.previewImageTarget.src = url
      this.previewTarget.classList.remove('hidden')
    }
  }

  updateCost(costCents) {
    if (this.hasCostTarget && costCents > 0) {
      const dollars = (costCents / 100).toFixed(2)
      this.costTarget.textContent = `$${dollars}`
      this.costTarget.classList.remove('hidden')
    }
  }

  setGenerating(isGenerating) {
    this.isGenerating = isGenerating

    if (this.hasGenerateBtnTarget) {
      this.generateBtnTarget.disabled = isGenerating
    }

    if (this.hasSpinnerTarget) {
      if (isGenerating) {
        this.spinnerTarget.classList.remove('hidden')
      } else {
        this.spinnerTarget.classList.add('hidden')
      }
    }
  }

  setStatus(message, isError = false) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
      this.statusTarget.classList.remove('hidden', 'text-red-600', 'text-green-600', 'text-slate-500')
      this.statusTarget.classList.add(isError ? 'text-red-600' : 'text-slate-500')
    }
  }

  clearStatus() {
    if (this.hasStatusTarget) {
      this.statusTarget.classList.add('hidden')
    }
  }

  getCSRFToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute('content') : ''
  }
}
